import React from 'react'
import classnames from 'classnames'

// SABER status pill. Follows the Tailwind `dark:` conventions in
// src/components/Badge.tsx but maps SABER's completeness / freshness / severity
// vocabularies onto a small color set. Season and TOU enums use separate palettes
// so adjacent columns in the same table (e.g. SUMMER + PEAK) never share a color.
export type SaberSemanticColor = 'green' | 'yellow' | 'red' | 'gray'

export type SaberDecorativeColor = 'indigo' | 'sky' | 'violet' | 'emerald' | 'orange' | 'cyan' | 'fuchsia' | 'teal'

export type SaberStatusColor = SaberSemanticColor | SaberDecorativeColor

/** Disambiguates season vs TOU columns when both use StatusBadge in one table. */
export type StatusBadgePalette = 'season' | 'tou' | 'utility' | 'tariff'

export interface StatusBadgeProps {
  status?: string
  className?: string
  /** Native tooltip shown on hover (e.g. a variance-category explanation). */
  title?: string
  /** When set, uses the season- or TOU-specific color map (disjoint palettes). */
  palette?: StatusBadgePalette
}

const STATUS_COLOR_MAP: Record<SaberSemanticColor, string[]> = {
  green: ['OK', 'FRESH', 'SUCCEEDED', 'SUCCESS', 'INFO', 'LOW'],
  yellow: ['WARN', 'WARNING', 'STALE', 'RUNNING', 'MEDIUM', 'PARTIAL'],
  red: ['CRITICAL', 'MISSING', 'FAILED', 'ERROR', 'HIGH'],
  gray: ['SKIPPED', 'UNKNOWN', 'PENDING']
}

/** TariffSeason — warm/cool tones, disjoint from TOU_VOCABULARY_MAP. */
const SEASON_VOCABULARY_MAP: Record<string, SaberDecorativeColor> = {
  SUMMER: 'orange',
  WINTER: 'indigo',
  ALL: 'cyan'
}

/** TariffTOUPeriod — disjoint from season colors so a SUMMER + PEAK row reads clearly. */
const TOU_VOCABULARY_MAP: Record<string, SaberDecorativeColor> = {
  PEAK: 'emerald',
  MID_PEAK: 'violet',
  PARTIAL_PEAK: 'fuchsia',
  OFF_PEAK: 'sky',
  SUPER_OFF_PEAK: 'teal',
  ALL: 'fuchsia'
}

/** California IOU short names — fixed colors so PG&E / SCE / SDG&E never collide. */
const UTILITY_VOCABULARY_MAP: Record<string, SaberDecorativeColor> = {
  'PG&E': 'orange',
  PGE: 'orange',
  SCE: 'sky',
  'SDG&E': 'violet',
  SDGE: 'violet'
}

/** Common IOU tariff schedules — keyed by normalizeTariffSchedule() so B-19 and B19 share a color. */
const TARIFF_VOCABULARY_MAP: Record<string, SaberDecorativeColor> = {
  B1: 'indigo',
  B6: 'fuchsia',
  B10: 'orange',
  B19: 'teal',
  B19S: 'violet',
  B20: 'cyan',
  E19: 'emerald',
  E19S: 'sky',
  E20: 'teal',
  A10: 'emerald',
  A19: 'orange',
  TOUGS1: 'emerald',
  TOUGS2: 'cyan',
  TOUGS3: 'fuchsia',
  GS2D: 'sky',
  ALTOU: 'violet'
}

/** Decorative-only palette — excludes semantic green/yellow/red so hashed labels never read as OK/WARN/CRITICAL. */
const DECORATIVE_COLORS: SaberDecorativeColor[] = [
  'indigo',
  'sky',
  'violet',
  'emerald',
  'orange',
  'cyan',
  'fuchsia',
  'teal'
]

const COLOR_CLASSES: Record<SaberStatusColor, string> = {
  green: 'bg-green-100 dark:bg-green-400/10 text-green-800 dark:text-green-400 ring-green-600/40 dark:ring-green-400/20',
  yellow: 'bg-yellow-100 dark:bg-yellow-400/10 text-yellow-800 dark:text-yellow-400 ring-yellow-600/30 dark:ring-yellow-400/30',
  red: 'bg-red-100 dark:bg-red-400/10 text-red-800 dark:text-red-400 ring-red-600/20 dark:ring-red-400/50',
  gray: 'bg-gray-50 dark:bg-gray-400/10 text-gray-800 dark:text-gray-400 ring-gray-600/10 dark:ring-gray-400/20',
  indigo: 'bg-indigo-100 dark:bg-indigo-400/10 text-indigo-800 dark:text-indigo-400 ring-indigo-600/30 dark:ring-indigo-400/30',
  sky: 'bg-sky-100 dark:bg-sky-400/10 text-sky-800 dark:text-sky-400 ring-sky-600/30 dark:ring-sky-400/30',
  violet: 'bg-violet-100 dark:bg-violet-400/10 text-violet-800 dark:text-violet-400 ring-violet-600/30 dark:ring-violet-400/30',
  emerald: 'bg-emerald-100 dark:bg-emerald-400/10 text-emerald-800 dark:text-emerald-400 ring-emerald-600/30 dark:ring-emerald-400/30',
  orange: 'bg-orange-100 dark:bg-orange-400/10 text-orange-800 dark:text-orange-400 ring-orange-600/30 dark:ring-orange-400/30',
  cyan: 'bg-cyan-100 dark:bg-cyan-400/10 text-cyan-800 dark:text-cyan-400 ring-cyan-600/30 dark:ring-cyan-400/30',
  fuchsia: 'bg-fuchsia-100 dark:bg-fuchsia-400/10 text-fuchsia-800 dark:text-fuchsia-400 ring-fuchsia-600/30 dark:ring-fuchsia-400/30',
  teal: 'bg-teal-100 dark:bg-teal-400/10 text-teal-800 dark:text-teal-400 ring-teal-600/30 dark:ring-teal-400/30'
}

/** Stable lookup key for tariff schedules (B-19-S → B19S). */
export const normalizeTariffSchedule = (schedule: string): string =>
  schedule.trim().toUpperCase().replace(/[-_\s]/g, '')

const lookupDomainColor = (upper: string, palette?: StatusBadgePalette, rawStatus?: string): SaberDecorativeColor | undefined => {
  if (palette === 'season') {
    return SEASON_VOCABULARY_MAP[upper]
  }
  if (palette === 'tou') {
    return TOU_VOCABULARY_MAP[upper]
  }
  if (palette === 'utility') {
    return UTILITY_VOCABULARY_MAP[upper]
  }
  if (palette === 'tariff' && rawStatus != null) {
    return TARIFF_VOCABULARY_MAP[normalizeTariffSchedule(rawStatus)]
  }
  return SEASON_VOCABULARY_MAP[upper] ?? TOU_VOCABULARY_MAP[upper] ?? UTILITY_VOCABULARY_MAP[upper]
}

/** Deterministic string hash — same status always maps to the same decorative slot. */
export const hashStatusColor = (status: string): number => {
  const normalized = status.trim().toUpperCase()
  let h = 0
  for (let i = 0; i < normalized.length; i++) {
    h = (Math.imul(31, h) + normalized.charCodeAt(i)) | 0
  }
  return Math.abs(h)
}

export const resolveColor = (status: string, palette?: StatusBadgePalette): SaberStatusColor => {
  const upper = status.trim().toUpperCase()
  for (const color of Object.keys(STATUS_COLOR_MAP) as SaberSemanticColor[]) {
    if (STATUS_COLOR_MAP[color].includes(upper)) {
      return color
    }
  }
  const domain = lookupDomainColor(upper, palette, status)
  if (domain != null) {
    return domain
  }
  const hashInput = palette === 'tariff' ? normalizeTariffSchedule(status) : upper
  return DECORATIVE_COLORS[hashStatusColor(hashInput) % DECORATIVE_COLORS.length]
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({ status, className, title, palette }) => {
  if (status == null || status === '') {
    return null
  }

  return (
    <span
      title={title}
      className={classnames(
        'mr-2 inline-flex items-center px-2 py-1 ring-1 ring-inset rounded-full text-xs font-medium',
        COLOR_CLASSES[resolveColor(status, palette)],
        className
      )}
    >
      {status.replaceAll('_', ' ')}
    </span>
  )
}

export default StatusBadge
